import React from 'react';
import { motion } from 'framer-motion';
import { FiCalendar, FiUsers, FiAward, FiGlobe } from 'react-icons/fi';

// Animatie varianten
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

// Tijdlijn item interface
interface TimelineItem {
  year: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  image?: string;
}

const History: React.FC = () => {
  // Tijdlijn data
  const timelineItems: TimelineItem[] = [
    {
      year: "2005",
      title: "Oprichting Lions Club Hoorn De Doelen",
      description: "Lions Club Hoorn De Doelen werd opgericht door een groep betrokken mannen uit Hoorn die zich wilden inzetten voor de lokale gemeenschap. De club werd vernoemd naar het historische gebouw 'De Doelen' in het centrum van Hoorn.",
      icon: <FiUsers />,
      image: "/images/history-founding.jpg"
    },
    {
      year: "2007",
      title: "Eerste Boekenmarkt",
      description: "De eerste editie van onze jaarlijkse boekenmarkt werd georganiseerd, wat zou uitgroeien tot een van onze meest succesvolle fundraising evenementen. De opbrengst ging naar het Westfries Gasthuis.",
      icon: <FiCalendar />,
      image: "/images/history-boekenmarkt.jpg"
    },
    {
      year: "2010",
      title: "Start samenwerking met Hospice Dignitas",
      description: "We begonnen onze langdurige samenwerking met Hospice Dignitas in Hoorn, een organisatie die we tot op de dag van vandaag blijven ondersteunen met donaties en praktische hulp.",
      icon: <FiAward />,
      image: "/images/history-hospice.jpg"
    },
    {
      year: "2013",
      title: "Eerste Bridgetoernooi voor het Goede Doel",
      description: "In samenwerking met Bridgeclub Zwaag / Risdam organiseerden we het eerste bridgetoernooi om geld in te zamelen voor lokale goede doelen. Dit evenement is sindsdien een jaarlijkse traditie geworden.",
      icon: <FiCalendar />
    },
    {
      year: "2015",
      title: "10-jarig jubileum",
      description: "We vierden ons 10-jarig bestaan met een speciaal evenement waarbij we terugblikten op onze prestaties en vooruit keken naar toekomstige projecten. Tijdens dit jubileum werd een speciale donatie gedaan aan het Jeugdfonds Sport & Cultuur.",
      icon: <FiAward />,
      image: "/images/history-10years.jpg"
    },
    {
      year: "2018",
      title: "Start deelname aan Sight First programma",
      description: "Lions Club Hoorn De Doelen sloot zich aan bij het internationale Sight First programma van Lions Clubs International, gericht op het voorkomen van vermijdbare blindheid wereldwijd.",
      icon: <FiGlobe />
    },
    {
      year: "2020",
      title: "Aanpassing tijdens COVID-19 pandemie",
      description: "Tijdens de COVID-19 pandemie pasten we onze activiteiten aan en richtten we ons op het ondersteunen van kwetsbare groepen in onze gemeenschap die extra hard getroffen werden door de crisis.",
      icon: <FiUsers />
    },
    {
      year: "2022",
      title: "Actie 'Warmte voor Oekraïne'",
      description: "Na de Russische invasie in Oekraïne sloten we ons aan bij de landelijke Lions actie 'Warmte voor Oekraïne' om hulp te bieden aan de getroffen bevolking tijdens de wintermaanden.",
      icon: <FiGlobe />,
      image: "/images/history-ukraine.jpg"
    },
    {
      year: "2023",
      title: "Recordopbrengst Boekenmarkt",
      description: "Onze jaarlijkse boekenmarkt bracht een recordbedrag van €5.000 op, dat werd verdeeld onder verschillende lokale goede doelen in de regio Hoorn.",
      icon: <FiAward />
    },
    {
      year: "2025",
      title: "20-jarig jubileum",
      description: "We kijken uit naar ons 20-jarig jubileum, waarbij we onze geschiedenis zullen vieren en nieuwe doelen zullen stellen voor de toekomst van Lions Club Hoorn De Doelen.",
      icon: <FiCalendar />
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-lions-dark text-white py-20">
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/history-hero.jpg" 
            alt="Geschiedenis van Lions Club Hoorn De Doelen" 
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.h1 
              variants={fadeIn}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              Onze <span className="text-lions-yellow">Geschiedenis</span>
            </motion.h1>
            <motion.p 
              variants={fadeIn}
              className="text-xl mb-8 text-gray-200"
            >
              Ontdek de rijke historie van Lions Club Hoorn De Doelen sinds onze oprichting in 2005 en hoe we zijn uitgegroeid tot een actieve serviceclub in de regio Hoorn.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 bg-white dark:bg-lions-dark">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <motion.div variants={fadeIn}>
              <h2 className="text-3xl font-bold mb-6 text-lions-blue dark:text-lions-yellow">
                Een traditie van service
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Lions Club Hoorn De Doelen werd in 2005 opgericht als onderdeel van Lions Clubs International, 
                de grootste serviceclub ter wereld. Onze club is vernoemd naar het historische gebouw 'De Doelen' 
                in het centrum van Hoorn, dat symbool staat voor samenkomst en gemeenschapszin.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Sinds onze oprichting hebben we ons ingezet voor diverse goede doelen in de regio Hoorn en daarbuiten. 
                Door de jaren heen zijn we gegroeid in ledenaantal en impact, maar onze missie is altijd hetzelfde gebleven: 
                het dienen van onze gemeenschap en het maken van een positief verschil in de levens van anderen.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                Op deze pagina nemen we u mee door de belangrijkste momenten in onze geschiedenis, 
                van onze oprichting tot de huidige dag, en blikken we vooruit naar de toekomst.
              </p>
            </motion.div>
            <motion.div variants={fadeIn} className="relative">
              <img 
                src="/images/lions-history-group.jpg" 
                alt="Lions Club Hoorn De Doelen groepsfoto" 
                className="rounded-lg shadow-lg w-full"
              />
              <div className="absolute -bottom-6 -right-6 bg-lions-blue dark:bg-lions-yellow text-white dark:text-lions-dark p-4 rounded-lg shadow-lg">
                <p className="font-bold text-xl">18 jaar</p>
                <p>Actief in Hoorn</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-16 bg-lions-light dark:bg-gray-900">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.h2 
              variants={fadeIn}
              className="text-3xl font-bold mb-4 text-lions-blue dark:text-lions-yellow"
            >
              Onze Tijdlijn
            </motion.h2>
            <motion.p 
              variants={fadeIn}
              className="text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto"
            >
              Belangrijke momenten in de geschiedenis van Lions Club Hoorn De Doelen
            </motion.p>
          </motion.div>

          <div className="relative">
            {/* Verticale lijn */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-lions-blue dark:bg-lions-yellow"></div>

            {/* Tijdlijn items */}
            {timelineItems.map((item, index) => (
              <motion.div
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={staggerContainer}
                className={`mb-12 md:mb-24 relative ${
                  index % 2 === 0 ? 'md:text-right' : 'md:text-left'
                }`}
              >
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  {/* Content */}
                  <motion.div 
                    variants={fadeIn}
                    className={`${index % 2 === 0 ? 'md:order-1' : 'md:order-2'}`}
                  >
                    <div className={`bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg ${
                      index % 2 === 0 ? 'md:ml-auto' : ''
                    }`}>
                      <div className="flex items-center mb-4">
                        <div className="bg-lions-blue dark:bg-lions-yellow text-white dark:text-lions-dark rounded-full w-10 h-10 flex items-center justify-center mr-3">
                          {item.icon}
                        </div>
                        <div>
                          <span className="text-sm text-gray-500 dark:text-gray-400 block">{item.year}</span>
                          <h3 className="text-xl font-bold text-lions-blue dark:text-lions-yellow">{item.title}</h3>
                        </div>
                      </div>
                      <p className={`text-gray-700 dark:text-gray-300 ${
                        index % 2 === 0 ? 'md:text-right' : 'md:text-left'
                      }`}>
                        {item.description}
                      </p>
                    </div>
                  </motion.div>

                  {/* Image (if available) */}
                  {item.image && (
                    <motion.div 
                      variants={fadeIn}
                      className={`${index % 2 === 0 ? 'md:order-2' : 'md:order-1'}`}
                    >
                      <img 
                        src={item.image} 
                        alt={item.title} 
                        className="rounded-lg shadow-lg w-full h-64 object-cover"
                      />
                    </motion.div>
                  )}

                  {/* If no image, show empty space */}
                  {!item.image && (
                    <div className={`${index % 2 === 0 ? 'md:order-2' : 'md:order-1'}`}></div>
                  )}
                </div>

                {/* Dot on timeline (visible on md screens) */}
                <div className="hidden md:block absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-5 h-5 bg-lions-yellow dark:bg-lions-blue rounded-full border-4 border-white dark:border-gray-900"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lions International Section */}
      <section className="py-16 bg-white dark:bg-lions-dark">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <motion.div variants={fadeIn} className="order-2 md:order-1">
              <h2 className="text-3xl font-bold mb-6 text-lions-blue dark:text-lions-yellow">
                Deel van een wereldwijde beweging
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Lions Club Hoorn De Doelen maakt deel uit van Lions Clubs International, 
                opgericht in 1917 door Melvin Jones. Wat begon als een droom om gemeenschappen 
                te verbeteren, is uitgegroeid tot 's werelds grootste serviceclub organisatie 
                met meer dan 1,4 miljoen leden in 48.000 clubs verspreid over 200 landen.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Het motto van Lions Clubs International is "We Serve" - wij dienen. Dit komt tot 
                uiting in de talloze projecten die Lions wereldwijd ondersteunen, van het bestrijden 
                van blindheid en honger tot het bieden van noodhulp bij rampen en het ondersteunen 
                van jeugdprogramma's.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                Als lid van deze wereldwijde organisatie delen we de waarden en doelen van Lions 
                Clubs International, terwijl we ons richten op de specifieke behoeften van onze 
                lokale gemeenschap in Hoorn en omgeving.
              </p>
            </motion.div>
            <motion.div variants={fadeIn} className="order-1 md:order-2">
              <img 
                src="/images/lions-international.jpg" 
                alt="Lions Clubs International" 
                className="rounded-lg shadow-lg w-full"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Future Section */}
      <section className="py-16 bg-lions-blue dark:bg-gray-900 text-white">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.h2 
              variants={fadeIn}
              className="text-3xl font-bold mb-6 text-white dark:text-lions-yellow"
            >
              Onze toekomst
            </motion.h2>
            <motion.p 
              variants={fadeIn}
              className="text-lg mb-8"
            >
              Terwijl we terugblikken op onze geschiedenis, kijken we ook vooruit naar de toekomst. 
              Lions Club Hoorn De Doelen blijft zich inzetten voor het welzijn van onze gemeenschap, 
              met nieuwe projecten en initiatieven die aansluiten bij de veranderende behoeften van onze tijd.
            </motion.p>
            <motion.p 
              variants={fadeIn}
              className="text-lg mb-8"
            >
              In 2025 vieren we ons 20-jarig jubileum, een mijlpaal die we zullen markeren met speciale 
              evenementen en projecten. We nodigen u uit om deel uit te maken van onze toekomst, 
              hetzij als lid, sponsor of supporter van onze activiteiten.
            </motion.p>
            <motion.div variants={fadeIn}>
              <a 
                href="/lid-worden" 
                className="btn-secondary bg-white text-lions-blue hover:bg-lions-yellow hover:text-white dark:bg-lions-yellow dark:text-lions-dark dark:hover:bg-white"
              >
                Word lid
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default History; 